<template>
  <BaseModal
    use-title-border
    ui-refresh>
    <template #title>
      <b>Report Content</b>
    </template>
    <template #body>
      <div
        v-if="submitted"
        class="text-center py-lgp">
        <SvgCheck
          height="48"
          class="text-success mb-md"/>
        <h3 class="mb-xs">
          Report Submitted
        </h3>
        <p class="text-muted">
          Thank you for helping keep our community safe.
        </p>
      </div>
      <div
        v-else-if="loading"
        class="text-center py-lgp">
        <SvgSpinner height="40"/>
      </div>
      <div v-else>
        <p class="my-md">
          Help us understand what's wrong with this content. Your report is confidential.
        </p>
        <div class="d-flex flex-wrap gap-xs">
          <BasePill
            v-for="option in reasonOptions"
            :key="option.value"
            v-model="reason"
            type="radio"
            name="report-reason"
            :value="option.value"
            :text="option.label"/>
        </div>
        <div class="mt-md">
          <label
            for="report-details"
            class="form-label">
            Additional details{{ detailsRequired ? '' : ' (optional)' }}
          </label>
          <BaseTextArea
            id="report-details"
            :value="details"
            :validations="v$.details"
            rows="3"
            placeholder="Tell us more about why you're reporting this content..."
            @input="details = $event"/>
        </div>
        <p
          v-if="errorMessage"
          class="text-danger mt-xs">
          {{ errorMessage }}
        </p>
      </div>
    </template>
    <template #footer>
      <div class="d-flex justify-content-center">
        <BaseButton
          v-if="submitted"
          text="Done"
          @click="closeModal"/>
        <BaseButton
          v-else-if="!loading"
          :text="submitting ? 'Submitting...' : 'Submit Report'"
          :disabled="!reason || submitting"
          @click="submitReport"/>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useOverlaysStore } from '@/stores/overlays.js'
import { getReportReasons } from '@shared/api/community.js'
import BaseModal from '@/components/global/BaseModal.vue'
import BaseButton from '@shared/components/ADORN/BaseButton.vue'
import BaseTextArea from '@/components/global/BaseTextArea.vue'
import SvgCheck from '@/components/global/svg/SvgCheck.vue'
import SvgSpinner from '@/components/global/svg/SvgSpinner.vue'
import BasePill from '@shared/components/ADORN/BasePill.vue'
import useVuelidate from '@vuelidate/core'
import { maxLength, requiredIf } from '@vuelidate/validators'
import { useCommunityStore } from '@/stores/community.js'
import { useLogger } from 'vue-logger-plugin'

const { reportFeedItem } = useCommunityStore()
const overlaysStore = useOverlaysStore()
const { modalContext } = storeToRefs(overlaysStore)
const { closeModal } = overlaysStore

const reason = ref('')
const details = ref('')
const submitting = ref(false)
const submitted = ref(false)
const loading = ref(true)
const errorMessage = ref('')
const reasonOptions = ref([])

const detailsRequired = computed(() => reason.value === 'other')
const rules = computed(() => ({
  details: {
    required: requiredIf(detailsRequired),
    maxLength: maxLength(1000)
  }
}))

const v$ = useVuelidate(rules, { details })

onMounted(async () => {
  try {
    const response = await getReportReasons()
    reasonOptions.value = response.data
  } catch {
    errorMessage.value = 'Failed to load report options. Please try again.'
  } finally {
    loading.value = false
  }
})

const logger = useLogger()
async function submitReport () {
  if (!reason.value) {
    errorMessage.value = 'Please select a reason for your report.'
    return
  }

  const isValid = await v$.value.$validate()
  if (!isValid) {
    return
  }

  submitting.value = true
  errorMessage.value = ''

  try {
    await reportFeedItem({
      feedItemId: modalContext.value.feedItemId,
      reason: reason.value,
      details: details.value
    })
    submitted.value = true
  } catch(e) {
    logger.error(e)
    errorMessage.value = 'Something went wrong. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.base-pill {
  flex: 1 1 48%;
}

.text-success {
  color: $success;
}
</style>
